import * as React from "react";
import { MoreHorizontal, ChevronDown } from "lucide-react";
import { DropdownMenu, MenuItem } from "../../../DropdownMenu";
import { cardAttention } from "../../../../styles/cardAttention.css";
import * as s from "./ListViewCard.style.css";
import type { ListViewCardBadge, ListViewCardBadgeAction, ListViewCardCTA, ListViewCardProps, ListViewCTAGroup } from "./ListViewCard.types";
import { useT } from "../../../../../app/lib/i18n";

export type {
  ListViewCardProps,
  ListViewCardBadge,
  ListViewCardBadgeAction,
  ListViewCardAction,
  ListViewCardCTA,
  ListViewCardCTAVariant,
  ListViewCardLayout,
  ListViewCardImageFormat,
  ListViewCardVariant,
  ListViewCTAGroup,
  ListViewCTAGroupImage,
} from "./ListViewCard.types";

export function ListViewCard(props: ListViewCardProps) {
  const t = useT();
  const {
    layout,
    imageFormat,
    image,
    title,
    subtitle,
    badges,
    badgeRowLeading,
    badgeRowTrailing,
    footerText,
    titleEnd,
    onClick,
    contextActions,
    actionsDisabled,
    variant = "default",
    ariaLabel,
    ctaGroups,
    animatedHighlight,
    highlightColor,
    compact,
  } = props;

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [badgeMenuOpenCount, setBadgeMenuOpenCount] = React.useState(0);
  /** Suppress card navigation right after a badge/context menu closes (avoids click-through). */
  const suppressClickUntilRef = React.useRef(0);

  const anyMenuOpen = menuOpen || badgeMenuOpenCount > 0;

  const handleBadgeOpenChange = React.useCallback((open: boolean) => {
    setBadgeMenuOpenCount((n) => n + (open ? 1 : -1));
    if (!open) {
      suppressClickUntilRef.current = Date.now() + 300;
    }
  }, []);

  const handleContextMenuOpenChange = React.useCallback((open: boolean) => {
    setMenuOpen(open);
    if (!open) {
      suppressClickUntilRef.current = Date.now() + 300;
    }
  }, []);

  const isClickable = variant !== "static" && Boolean(onClick);
  const hasMenu = Boolean(contextActions?.length);
  const highlightStyle = animatedHighlight && highlightColor
    ? { [s.highlightColorProperty]: highlightColor } as React.CSSProperties
    : undefined;

  const deckClass = [
    s.cardDeck,
    isClickable ? s.cardDeckClickable : "",
    variant === "attention" ? cardAttention : "",
    anyMenuOpen ? s.cardMenuOpen : "",
    animatedHighlight ? s.cardAnimatedHighlight : "",
  ]
    .filter(Boolean)
    .join(" ");

  const stackClass = [
    s.cardStack,
    compact ? s.cardStackCompact : "",
    isClickable ? s.cardStackClickable : "",
    variant === "attention" ? cardAttention : "",
    anyMenuOpen ? s.cardMenuOpen : "",
    animatedHighlight ? s.cardAnimatedHighlight : "",
  ]
    .filter(Boolean)
    .join(" ");

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!isClickable) return;
    // Don't navigate while a context/badge menu is open, or when the event
    // originated from an interactive control inside the card (role dropdown, etc.).
    if (anyMenuOpen) return;
    if (Date.now() < suppressClickUntilRef.current) return;
    if (
      "target" in e &&
      (e.target as HTMLElement).closest?.(
        'button,a,input,select,textarea,label,[role="menuitem"],[role="menu"]',
      )
    ) {
      return;
    }
    onClick?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick(e);
    }
  };

  const titleNode = titleEnd ? (
    <div className={s.titleRow}>
      <p className={s.cardTitle}>{title}</p>
      <span className={s.titleEnd}>{titleEnd}</span>
    </div>
  ) : (
    <p className={s.cardTitle}>{title}</p>
  );

  const contextMenu = hasMenu ? (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={s.contextBtn}
        aria-label={t("shared.listView.moreOptions")}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        disabled={actionsDisabled}
        onClick={(e) => {
          e.stopPropagation();
          handleContextMenuOpenChange(!menuOpen);
        }}
      >
        <MoreHorizontal size={14} />
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={menuOpen}
        setOpen={handleContextMenuOpenChange}
        onChange={(id) => {
          const action = contextActions!.find((a) => a.id === id);
          action?.onClick();
        }}
        closeOnSelect
      >
        {contextActions!.map((action) => (
          <MenuItem key={action.id} value={action.id} disabled={action.disabled}>
            <span className={action.destructive ? s.contextBtnDangerItem : undefined}>
              {action.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  ) : null;

  const badgeEls =
    (badges?.length || badgeRowLeading || badgeRowTrailing) ? (
      <div className={s.badgeRow}>
        {badgeRowLeading}
        {badges?.map((b) => {
          const cls = [s.badge, getBadgeVariantClass(b.variant, b.color)].filter(Boolean).join(" ");
          const colorStyle = b.color ? { backgroundColor: `${b.color}18`, color: b.color } : undefined;
          if (b.actions?.length) {
            return (
              <BadgeWithMenu key={b.id} badge={b as ListViewCardBadge & { actions: ListViewCardBadgeAction[] }} badgeClass={cls} colorStyle={colorStyle} onOpenChange={handleBadgeOpenChange} />
            );
          }
          return (
            <span key={b.id} className={cls} style={colorStyle} title={b.title}>
              {b.icon}
              {b.label}
            </span>
          );
        })}
        {badgeRowTrailing ? (
          <div className={s.badgeRowTrailing}>{badgeRowTrailing}</div>
        ) : null}
      </div>
    ) : null;

  // CTA overlay is rendered by the ListView CardWrapper/RowWrapper as a sibling to this card,
  // so it can sit above the wrapper-level context button (which is outside this element's stacking context).

  if (layout === "deck") {
    if (compact) {
      return (
        <div
          className={deckClass}
          style={highlightStyle}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          aria-label={ariaLabel}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          <div className={s.deckBodyCompact}>
            {imageFormat !== "none" && image}
            <div className={s.deckBodyCompactText}>
              <p className={s.cardTitle}>{title}</p>
              {badgeEls}
            </div>
          </div>
          {contextMenu}
        </div>
      );
    }
    return (
      <div
        className={deckClass}
        style={highlightStyle}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {imageFormat !== "none" && (
          <div className={s.cover}>{image}</div>
        )}
        <div className={s.deckBody}>
          {titleNode}
          {typeof subtitle === "string" ? (
            subtitle ? <p className={s.cardSubtitle}>{subtitle}</p> : null
          ) : subtitle != null ? (
            <div className={s.cardSubtitleSlot}>{subtitle}</div>
          ) : null}
          {badgeEls}
          {footerText && <span className={s.deckFooter}>{footerText}</span>}
        </div>
        {contextMenu}
      </div>
    );
  }

  return (
    <div
      className={stackClass}
      style={highlightStyle}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {imageFormat !== "none" && (
        <div
          className={[
            s.stackImageWrap,
            compact ? s.stackImageWrapCompact : "",
            imageFormat === "cover" ? s.stackImageWrapCover : s.stackImageWrapPadded,
          ].filter(Boolean).join(" ")}
        >
          {image}
        </div>
      )}
      <div
        className={
          compact
            ? s.stackBodyCompact
            : hasMenu
              ? s.stackBody
              : s.stackBodyNoPad
        }
      >
        {titleNode}
        {!compact &&
          (typeof subtitle === "string" ? (
            subtitle ? <p className={s.cardSubtitle}>{subtitle}</p> : null
          ) : subtitle != null ? (
            <div className={s.cardSubtitleSlot}>{subtitle}</div>
          ) : null)}
        {badgeEls}
        {!compact && !titleEnd && footerText && (
          <span className={s.stackFooter}>{footerText}</span>
        )}
      </div>
      {contextMenu}
    </div>
  );
}

function BadgeWithMenu({
  badge,
  badgeClass,
  colorStyle,
  onOpenChange,
}: {
  badge: ListViewCardBadge & { actions: ListViewCardBadgeAction[] };
  badgeClass: string;
  colorStyle?: React.CSSProperties;
  onOpenChange: (open: boolean) => void;
}) {
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);

  const handleSetOpen = React.useCallback((next: boolean | ((prev: boolean) => boolean)) => {
    setOpen((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      if (value !== prev) onOpenChange(value);
      return value;
    });
  }, [onOpenChange]);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={[badgeClass, s.badgeClickable].filter(Boolean).join(" ")}
        style={colorStyle}
        title={badge.title}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleSetOpen((prev) => !prev);
        }}
      >
        {badge.icon}
        {badge.label}
        <ChevronDown size={10} />
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={handleSetOpen}
        onChange={(id) => {
          const action = badge.actions.find((a) => a.id === id);
          action?.onClick();
        }}
        closeOnSelect
      >
        {badge.actions.map((action) => (
          <MenuItem key={action.id} value={action.id} disabled={action.disabled}>
            <span
              className={action.destructive ? s.contextBtnDangerItem : undefined}
              style={action.color ? { color: action.color } : undefined}
            >
              {action.label}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  );
}

function getCtaBtnClass(variant?: string): string {
  switch (variant) {
    case "danger": return s.ctaBtnDanger;
    case "secondary": return s.ctaBtnSecondary;
    default: return s.ctaBtnPrimary;
  }
}

function getBadgeVariantClass(
  variant?: string,
  color?: string,
): string | undefined {
  if (color) return undefined; // inline style handles custom colors
  switch (variant) {
    case "active":
      return s.badgeActive;
    case "pending":
      return s.badgePending;
    case "invited":
      return s.badgeInvited;
    case "disabled":
      return s.badgeDisabled;
    default:
      return undefined;
  }
}
